import { useState, useEffect, useRef } from 'react';
import casinoWS from '../services/websocket';

const WS_SERVER_URL = process.env.REACT_APP_WS_URL || 'wss://s-sino-casino.fly.dev';

function getUrlParams() {
  const params = new URLSearchParams(window.location.search);
  return {
    panelTicket: params.get('panelTicket') || null,
    token: params.get('token') || null,
    displayName: params.get('name') || params.get('displayName') || null,
    steamId: params.get('steamid') || null,
  };
}

const URL_PARAMS = getUrlParams();

export function useConnection() {
  const [isConnected, setIsConnected] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [loadingScreenHidden, setLoadingScreenHidden] = useState(false);
  const [connectionError, setConnectionError] = useState(null);
  const loadingStartTime = useRef(Date.now());

  const urlParams = URL_PARAMS;

  // Hide the HTML loading screen after fully initialized
  useEffect(() => {
    if (isConnected && isInitialized && window.hideLoadingScreen) {
      const MIN_LOADING_TIME = 1500;
      const elapsed = Date.now() - loadingStartTime.current;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsed);

      setTimeout(() => {
        window.hideLoadingScreen();
        setTimeout(() => setLoadingScreenHidden(true), 400);
      }, remainingTime);
    }
  }, [isConnected, isInitialized]);

  // Connect to WebSocket server
  useEffect(() => {
    const connect = async () => {
      const token = urlParams.token;
      const panelTicket = urlParams.panelTicket;

      if (!panelTicket && !token) {
        setConnectionError('Missing panel session. Reopen the casino panel from the game client.');
        return;
      }

      try {
        await casinoWS.connect(WS_SERVER_URL, panelTicket || token, {
          displayName: urlParams.displayName,
          steamId: urlParams.steamId,
          panelTicket,
        });
      } catch (err) {
        setConnectionError('Failed to connect to game server');
      }
    };

    connect();
    return () => { casinoWS.disconnect(); };
  }, [urlParams.panelTicket, urlParams.token, urlParams.displayName, urlParams.steamId]);

  // Subscribe to connection lifecycle events
  useEffect(() => {
    const unsubscribers = [];

    unsubscribers.push(casinoWS.on('connected', () => {
      setIsConnected(true);
      setConnectionError(null);
    }));

    unsubscribers.push(casinoWS.on('disconnected', () => {
      setIsConnected(false);
      setIsInitialized(false);
      if (window.showLoadingScreen) window.showLoadingScreen();
    }));

    unsubscribers.push(casinoWS.on('reconnecting', () => {
      setIsConnected(false);
      setIsInitialized(false);
      setConnectionError(null);
      if (window.showLoadingScreen) window.showLoadingScreen();
    }));

    unsubscribers.push(casinoWS.on('reconnected', () => {
      setIsConnected(true);
      setConnectionError(null);
      if (window.hideLoadingScreen) window.hideLoadingScreen();
    }));

    unsubscribers.push(casinoWS.on('error', (data) => {
      if (data.message && !data.message.toLowerCase().includes('rate limit')) {
        if (!casinoWS.isReconnecting) {
          setConnectionError(data.message);
        }
      }
    }));

    unsubscribers.push(casinoWS.on('init', () => {
      setIsInitialized(true);
    }));

    return () => unsubscribers.forEach(unsub => unsub());
  }, []);

  return { isConnected, isInitialized, loadingScreenHidden, connectionError };
}
